import type { Document } from "@freightsimple/generated-dashboard-openapi-client/models/Document";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CustomsDocsUploadMode } from "@/Apis/CustomsDocsUploadMode";
import type { UUID } from "@/Shared/types";
import type { CustomsDocsDropzoneProps } from "@/Shared/CustomDocs/CustomComponents/CustomDocsDropzone";
import { CustomDocsDropzone } from "@/Shared/CustomDocs/CustomComponents/CustomDocsDropzone";
import { CustomDocsUpdateList } from "@/Shared/CustomDocs/CustomComponents/CustomDocsUpdateList";

export type CurrentCustomsDocuments = Document[] | "loading" | "failed";

interface CustomDocsUpdateSubmitBodyProps extends CustomsDocsDropzoneProps {
  mode: CustomsDocsUploadMode | undefined;
  /**
   * The shipment's customs documents as they stand before this upload, or
   * where fetching them has got to.
   */
  documents: CurrentCustomsDocuments;
  onDownload: (documentId: UUID) => Promise<void>;
  errorMessage: string | undefined;
  uploading: boolean;
  uploaded: boolean;
  onBack: () => void;
  onCancel: () => void;
  onUpload: () => void;
}

function fileCountLabel(count: number): string {
  return count === 1 ? "1 file" : `${count} files`;
}

// Before the list has loaded, or if it failed, we can't count the documents
// Replace will delete — but the shipment has some (the dialog only opens when
// it does), so the warning still shows, just without the count.
function deletionWarning(documents: CurrentCustomsDocuments): string {
  if (!Array.isArray(documents)) {
    return "Your current customs documents will be deleted.";
  }
  return documents.length === 1
    ? "The document listed above will be deleted."
    : `The ${documents.length} documents listed above will be deleted.`;
}

function CurrentDocuments_({
  documents,
  onDownload,
}: {
  documents: CurrentCustomsDocuments;
  onDownload: (documentId: UUID) => Promise<void>;
}) {
  if (documents === "loading") {
    return (
      <T size="sm" color="muted">
        Loading your current documents…
      </T>
    );
  }
  if (documents === "failed") {
    return (
      <T size="sm" color="muted">
        We couldn't load your current documents.
      </T>
    );
  }
  return <CustomDocsUpdateList documents={documents} onDownload={onDownload} />;
}

export function CustomDocsUpdateSubmitBody({
  mode,
  documents,
  onDownload,
  files,
  setFiles,
  dropzoneState,
  errorMessage,
  uploading,
  uploaded,
  onBack,
  onCancel,
  onUpload,
}: CustomDocsUpdateSubmitBodyProps) {
  const replacing = mode === CustomsDocsUploadMode.Replace;
  // Only a list that loaded and came back empty proves there is nothing to
  // delete.
  const deletesDocuments =
    replacing && !(Array.isArray(documents) && documents.length === 0);
  const showsBanner = deletesDocuments || files.length > 0;

  return (
    <Stack gap={6}>
      <Stack gap={3} align="start">
        <Button size="link" previousArrow onClick={onBack}>
          Go back
        </Button>
        <Stack gap={1}>
          <Heading level={3}>
            {replacing ? "Replace existing documents" : "Add new documents"}
          </Heading>
          <T color="secondary">
            If you're not sure what documents you need, or to learn more, see
            our{" "}
            <Button
              size="link"
              inheritFont
              href="https://help.freightsimple.com/using-freightsimple/booking-your-shipment/uploading-your-customs-documents"
            >
              Help Center
            </Button>
            .
          </T>
        </Stack>
      </Stack>

      {/* Equal columns that stack once the dialog is too narrow for both, so
          the list and the dropzone carry the same weight. */}
      <Grid columns={2} minColumnWidth={240} gapX="md" gapY="sm">
        <Stack gap={3}>
          <Heading level={4}>Current documents</Heading>
          <CurrentDocuments_ documents={documents} onDownload={onDownload} />
        </Stack>
        <Stack gap={3}>
          <Heading level={4}>New documents</Heading>
          <CustomDocsDropzone
            files={files}
            setFiles={setFiles}
            dropzoneState={dropzoneState}
          />
        </Stack>
      </Grid>

      {(showsBanner || errorMessage !== undefined) && (
        // The banners are one block of messages, so they sit closer to each
        // other than to the rest of the step.
        <Stack gap={2}>
          {/* A failed upload is what the customer has to act on now, so it
              comes first. */}
          {errorMessage !== undefined && (
            <Banner variant="danger">{errorMessage}</Banner>
          )}
          {/* Replace can't be undone, so its warning takes the place of the
              verify banner rather than sitting beside it — one banner, and
              the stronger message wins. */}
          {deletesDocuments ? (
            <Banner
              variant="warning"
              title="Replacing Deletes Your Current Documents"
            >
              {deletionWarning(documents)} Check your new files before
              uploading.
            </Banner>
          ) : (
            files.length > 0 && (
              <Banner variant="info" title="Verify Your Files Before Uploading">
                Please ensure your files are accurate and complete before
                uploading!
              </Banner>
            )
          )}
        </Stack>
      )}

      <Row justify="end" gap={3} wrap>
        <Button onClick={onCancel}>Cancel</Button>
        {files.length > 0 && (
          <Button
            primary
            onClick={onUpload}
            loading={uploading}
            loadingSuccess={uploaded}
            disabled={uploading || uploaded}
            disabledMessage="Update is in progress"
          >
            {replacing
              ? `Replace with ${fileCountLabel(files.length)}`
              : `Upload ${fileCountLabel(files.length)}`}
          </Button>
        )}
      </Row>
    </Stack>
  );
}
